import React, { useMemo } from 'react';
import { AccessorKeyColumnDef, createColumnHelper, Row, RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import * as ROUTES from '../../../constants/routes';
import { Button } from 'react-bootstrap';
import { useUser } from '../../../hooks/use-user';
import { useNavigate } from 'react-router-dom';
import Table from './Table';

export type CompanyDataEntry = {
	name: string;
	private: boolean;
	dationIdentifiers: Array<{ id: number; dationInstance: { id: number; name: string } }>;
	primaryDationInstance: { id: number; name: string } | null;
};

const columnHelper = createColumnHelper<CompanyDataEntry>();

type Props = {
	companies: CompanyDataEntry[];
	handleRowClick: (row: RowData) => void;
};

const getTypeLabel = (row: CompanyDataEntry, shared: string, privateLabel: string): string => {
	if (!row.private) {
		return shared;
	}
	if (row.dationIdentifiers?.length > 1) {
		return [...row.dationIdentifiers]
			.sort((a, b) => a.dationInstance.name.localeCompare(b.dationInstance.name))
			.map((d) => d.dationInstance.name)
			.join(', ');
	}
	return privateLabel;
};

const CompaniesOverview = ({ companies, handleRowClick }: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { setCompany } = useUser();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor('name', {
					header: t('company.name'),
				}),
				columnHelper.accessor('primaryDationInstance', {
					header: t('company.head_trainer'),
					cell: (cellInfo) => cellInfo.getValue()?.name ?? '-',
				}),
				columnHelper.accessor('private', {
					header: t('company.type'),
					cell: (cellInfo) => {
						return getTypeLabel(
							cellInfo.row.original,
							t('company.shared'),
							t('company.private'),
						);
					},
					sortingFn: (rowA: Row<CompanyDataEntry>, rowB: Row<CompanyDataEntry>) => {
						const shared = t('company.shared');
						const privateLabel = t('company.private');
						const a = getTypeLabel(rowA.original, shared, privateLabel);
						const b = getTypeLabel(rowB.original, shared, privateLabel);
						return a.localeCompare(b);
					},
				}),
				columnHelper.display({
					id: 'seeCompany',
					cell: (cellInfo) => (
						<Button
							variant="outline-primary"
							type="button"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								setCompany(cellInfo.row.original);
								navigate(ROUTES.DASHBOARD);
							}}>
							{t('company.see')}
						</Button>
					),
				}),
			] as Array<AccessorKeyColumnDef<CompanyDataEntry, unknown>>,
		[t],
	);

	return <Table data={companies} columns={columns} handleRowClick={handleRowClick} />;
};

export default CompaniesOverview;
